import { useState, useEffect } from "react";
import { C, Card, CardHead, SectionTitle, Bar, Dot, GpuMini, Badge } from "./theme";
import { GET } from "./api";
import { useLang } from "./i18n";

export default function AdminOverview(props) {
  const { servers, vms, users, allGpus, metricsSummary, getServerIp, pendingCount } = props;
  const { t } = useLang();
  const [diskInfo, setDiskInfo] = useState(null);
  const [hostMetrics, setHostMetrics] = useState({});

  useEffect(() => {
    GET("/api/disk").then(setDiskInfo).catch(() => {});
    GET("/api/host-metrics").then(setHostMetrics).catch(() => {});
    const id = setInterval(() => {
      GET("/api/disk").then(setDiskInfo).catch(() => {});
      GET("/api/host-metrics").then(setHostMetrics).catch(() => {});
    }, 10000);
    return () => clearInterval(id);
  }, []);

  const runningVms = vms.filter(v => v.status === "running").length;
  const stoppedVms = vms.filter(v => v.status === "stopped").length;
  const reclaimedVms = vms.filter(v => v.status === "reclaimed").length;
  const usedGpus = allGpus.filter(g => g.assigned).length;
  const freeGpus = allGpus.length - usedGpus;

  const host = diskInfo?.host || {};
  const hostTotalGB = host.total_bytes ? (host.total_bytes / (1024**3)).toFixed(0) : "?";
  const hostUsedGB = host.used_bytes ? (host.used_bytes / (1024**3)).toFixed(0) : "?";
  const hostAvailGB = host.avail_bytes ? (host.avail_bytes / (1024**3)).toFixed(0) : "?";
  const hostPct = host.use_pct ? parseInt(host.use_pct) : 0;

  const statCards = [
    { label:t("users"), value:users.length, color:C.blue },
    { label:t("vm"), value:vms.length, sub:`${t("running")} ${runningVms} / ${t("stopped")} ${stoppedVms} / ${t("reclaimed")} ${reclaimedVms}`, color:C.green },
    { label:t("gpu"), value:`${usedGpus}/${allGpus.length}`, sub:`${t("assigned")} ${usedGpus} / ${t("free")} ${freeGpus}`, color:C.purple },
    { label:t("pending_requests"), value:pendingCount||0, color:pendingCount>0?C.red:C.dim2 },
  ];

  return (
    <div>
      <SectionTitle title={t("system_overview")}/>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))", gap:12, marginBottom:20 }}>
        {statCards.map(s => (
          <Card key={s.label} style={{ padding:"16px 18px", textAlign:"center" }}>
            <div style={{ fontSize:9, color:C.dim2, fontFamily:"monospace",
              letterSpacing:1, marginBottom:6, textTransform:"uppercase" }}>{s.label}</div>
            <div style={{ fontSize:26, fontWeight:700, color:s.color,
              fontFamily:"'Rajdhani',sans-serif" }}>{s.value}</div>
            {s.sub && <div style={{ fontSize:10, color:C.dim2, marginTop:4 }}>{s.sub}</div>}
          </Card>
        ))}
      </div>

      {/* Live VM metrics */}
      {Object.keys(metricsSummary).length > 0 && (
        <>
          <SectionTitle title={t("live_vm_metrics")}/>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:12, marginBottom:20 }}>
            {vms.filter(v => v.status === "running").map(vm => {
              const m = metricsSummary[vm.id];
              if (!m) return null;
              return (
                <Card key={vm.id} style={{ padding:14 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                    <span style={{ fontSize:11, color:C.cyan, fontFamily:"monospace", fontWeight:700 }}>{vm.id}</span>
                    <span style={{ fontSize:9, color:C.dim2 }}>{vm.server}</span>
                  </div>
                  <div style={{ display:"flex", gap:14, flexWrap:"wrap", fontSize:11, fontFamily:"monospace" }}>
                    <span style={{color:C.dim2}}>CPU <span style={{color:C.blue,fontWeight:700}}>{m.cpu_pct?.toFixed(1)}%</span></span>
                    <span style={{color:C.dim2}}>MEM <span style={{color:C.purple,fontWeight:700}}>
                      {m.mem_total_mb>0?`${(m.mem_used_mb/1024).toFixed(1)}/${(m.mem_total_mb/1024).toFixed(0)}G`:"--"}
                    </span></span>
                    <span style={{color:C.dim2}}>GPU <span style={{
                      color:m.gpu_util>=80?C.red:m.gpu_util>=50?C.amber:C.green,fontWeight:700
                    }}>{m.gpu_util>0?`${m.gpu_util?.toFixed(0)}%`:"--"}</span></span>
                    <span style={{color:C.dim2}}>NET <span style={{color:C.cyan,fontWeight:700}}>
                      RX {(m.net_rx_bytes/(1024*1024)).toFixed(0)}M TX {(m.net_tx_bytes/(1024*1024)).toFixed(0)}M
                    </span></span>
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Disk */}
      <SectionTitle title={t("disk_status")}/>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 2fr", gap:14, marginBottom:20 }}>
        <Card style={{ padding:18 }}>
          <div style={{ fontSize:9, color:C.dim2, fontFamily:"monospace",
            letterSpacing:1, marginBottom:10, textTransform:"uppercase" }}>{t("host_storage")}</div>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
            <span style={{ fontSize:11, color:C.text }}>{hostUsedGB} GB / {hostTotalGB} GB</span>
            <span style={{ fontSize:11, color:hostPct>80?C.red:hostPct>60?C.amber:C.green,
              fontWeight:700 }}>{hostPct}%</span>
          </div>
          <Bar pct={hostPct} color={hostPct>80?C.red:hostPct>60?C.amber:C.green} h={6}/>
          <div style={{ fontSize:10, color:C.dim2, marginTop:8 }}>
            {t("available")}: <span style={{ color:C.green, fontWeight:700 }}>{hostAvailGB} GB</span>
          </div>
        </Card>

        <Card style={{ padding:18 }}>
          <div style={{ fontSize:9, color:C.dim2, fontFamily:"monospace",
            letterSpacing:1, marginBottom:10, textTransform:"uppercase" }}>{t("vm_disk_usage")}</div>
          {diskInfo?.vms?.length > 0 ? (
            <div style={{ display:"grid", gap:8 }}>
              {diskInfo.vms.map(d => {
                const pct = d.allocated_gb > 0 ? (d.used_gb / d.allocated_gb) * 100 : 0;
                return (
                  <div key={d.vm_id} style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <span style={{ fontSize:10, color:C.cyan, width:100, flexShrink:0,
                      fontFamily:"monospace" }}>{d.vm_id}</span>
                    <div style={{ flex:1 }}>
                      <Bar pct={pct} color={pct>80?C.red:pct>50?C.amber:C.blue} h={4}/>
                    </div>
                    <span style={{ fontSize:10, color:C.text, width:120, textAlign:"right",
                      fontFamily:"monospace", flexShrink:0 }}>
                      {d.used_gb} / {d.allocated_gb} GB
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ color:C.dim2, fontSize:11 }}>{t("no_vm")}</div>
          )}
        </Card>
      </div>

      {/* Servers */}
      <SectionTitle title={t("server_status")}/>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(340px, 1fr))", gap:14 }}>
        {(servers||[]).map(srv => {
          const sGpus = (srv.gpus || []);
          const sVms = vms.filter(v => v.server === srv.id);
          const assignedGpus = sGpus.filter(g => g.assigned).length;

          const hm = hostMetrics[srv.id] || {};
          const cpuPct = hm.cpu_pct || 0;
          const memPct = hm.mem_total_mb > 0 ? (hm.mem_used_mb / hm.mem_total_mb * 100) : 0;
          const cpuColor = cpuPct > 80 ? C.red : cpuPct > 50 ? C.amber : C.green;
          const memColor = memPct > 80 ? C.red : memPct > 50 ? C.amber : C.blue;

          return (
            <Card key={srv.id}>
              <CardHead>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <Dot color={srv.online?C.green:C.red} pulse={srv.online}/>
                  <span style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:14,
                    fontWeight:700, color:C.text, letterSpacing:1 }}>{srv.label || srv.id}</span>
                  <Badge label={srv.online?"ONLINE":"OFFLINE"}
                    color={srv.online?C.green:C.red}/>
                </div>
                <span style={{ color:C.dim2, fontSize:10, fontFamily:"monospace" }}>
                  {getServerIp(srv.id)}
                </span>
              </CardHead>
              <div style={{ padding:14 }}>
                {/* Host metrics */}
                {hm.cpu_cores && (
                  <div style={{ marginBottom:14 }}>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:8, marginBottom:10 }}>
                      <div style={{ textAlign:"center" }}>
                        <div style={{ fontSize:8, color:C.dim2, letterSpacing:1 }}>CPU</div>
                        <div style={{ fontSize:16, fontWeight:700, color:cpuColor, fontFamily:"'Rajdhani',sans-serif" }}>{cpuPct.toFixed(0)}%</div>
                        <div style={{ fontSize:8, color:C.dim2 }}>{hm.cpu_cores}cores</div>
                      </div>
                      <div style={{ textAlign:"center" }}>
                        <div style={{ fontSize:8, color:C.dim2, letterSpacing:1 }}>RAM</div>
                        <div style={{ fontSize:16, fontWeight:700, color:memColor, fontFamily:"'Rajdhani',sans-serif" }}>{memPct.toFixed(0)}%</div>
                        <div style={{ fontSize:8, color:C.dim2 }}>{(hm.mem_used_mb/1024).toFixed(0)}/{(hm.mem_total_mb/1024).toFixed(0)}G</div>
                      </div>
                      <div style={{ textAlign:"center" }}>
                        <div style={{ fontSize:8, color:C.dim2, letterSpacing:1 }}>LOAD</div>
                        <div style={{ fontSize:16, fontWeight:700, color:hm.load_1m>hm.cpu_cores?C.red:hm.load_1m>hm.cpu_cores*0.7?C.amber:C.green, fontFamily:"'Rajdhani',sans-serif" }}>{hm.load_1m?.toFixed(1)}</div>
                        <div style={{ fontSize:8, color:C.dim2 }}>{hm.load_5m?.toFixed(1)} / {hm.load_15m?.toFixed(1)}</div>
                      </div>
                      <div style={{ textAlign:"center" }}>
                        <div style={{ fontSize:8, color:C.dim2, letterSpacing:1 }}>VM</div>
                        <div style={{ fontSize:16, fontWeight:700, color:C.blue, fontFamily:"'Rajdhani',sans-serif" }}>{hm.vm_running}</div>
                        <div style={{ fontSize:8, color:C.dim2 }}>/{hm.vm_total} total</div>
                      </div>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                      <div>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}>
                          <span style={{ fontSize:8, color:C.dim2 }}>CPU</span>
                          <span style={{ fontSize:8, color:cpuColor }}>{cpuPct.toFixed(0)}%</span>
                        </div>
                        <Bar pct={cpuPct} color={cpuColor} h={3}/>
                      </div>
                      <div>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}>
                          <span style={{ fontSize:8, color:C.dim2 }}>RAM</span>
                          <span style={{ fontSize:8, color:memColor }}>{memPct.toFixed(0)}%</span>
                        </div>
                        <Bar pct={memPct} color={memColor} h={3}/>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
                  {[
                    {l:"GPU", v:`${assignedGpus}/${sGpus.length}`, c:C.purple},
                    {l:"VM", v:sVms.length, c:C.blue},
                  ].map(s => (
                    <div key={s.l} style={{ textAlign:"center" }}>
                      <div style={{ fontSize:8, color:C.dim2, letterSpacing:1 }}>{s.l}</div>
                      <div style={{ fontSize:18, fontWeight:700, color:s.c,
                        fontFamily:"'Rajdhani',sans-serif" }}>{s.v}</div>
                    </div>
                  ))}
                </div>

                {sGpus.length > 0 && (
                  <div>
                    <div style={{ fontSize:8, color:C.dim2, letterSpacing:1, marginBottom:6 }}>GPU POOL</div>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(130px, 1fr))", gap:6 }}>
                      {sGpus.map(g => <GpuMini key={g.id} gpu={g}/>)}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
